import React from "react";

export type NavigationItem = "portfolio" | "cohorts" | "regions" | "industry";

interface SidebarProps {
  isOpen: boolean;
  activeNav: NavigationItem;
  onNavChange: (nav: NavigationItem) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  activeNav,
  onNavChange,
}) => {
  return (
    <>
      <aside
        className={`bg-gradient-to-b from-[#1a1f3a] to-[#050716] border-r border-[rgba(139,146,176,0.25)] p-5 flex flex-col gap-6 backdrop-blur-[18px] transition-transform duration-250 ease-out
          w-[20%] h-screen fixed left-0 top-0 overflow-y-auto
          max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:w-[230px] max-lg:z-20 max-lg:translate-x-[-100%]
          ${isOpen ? "max-lg:translate-x-0" : ""}
        `}
      >
        <div className="flex items-center justify-between gap-2.5">
          <div className="flex items-center gap-2.5">
            <div className="w-7.5 h-7.5 rounded-xl bg-gradient-to-br from-cyber-blue via-blockchain-purple via-neon-green to-cyber-blue shadow-[0_0_24px_rgba(0,212,255,0.4)] relative overflow-hidden">
              <div className="absolute inset-1.5 rounded-[10px] bg-gradient-to-br from-white/25 to-transparent mix-blend-screen" />
            </div>
            <div className="font-space-grotesk text-xl font-bold tracking-[0.14em] uppercase bg-gradient-to-r from-white to-cyber-blue bg-clip-text text-transparent whitespace-nowrap">
              CRYPSEY
            </div>
          </div>
          <span className="px-2.5 py-1 rounded-full border border-[rgba(0,255,136,0.35)] text-[11px] text-neon-green uppercase tracking-[0.09em] bg-gradient-to-br from-[rgba(0,255,136,0.2)] to-transparent">
            Data Console
          </span>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#8b92b0] mt-2 mb-1">
            Overview
          </div>
          <ul className="flex flex-col gap-1.5 list-none">
            <li
              onClick={() => onNavChange("portfolio")}
              className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-[10px] text-sm cursor-pointer transition-all duration-200 ${
                activeNav === "portfolio"
                  ? "text-white bg-gradient-to-r from-[rgba(0,212,255,0.35)] to-transparent shadow-[0_8px_30px_rgba(0,212,255,0.35)]"
                  : "text-[#8b92b0] hover:bg-[rgba(26,31,58,0.9)] hover:text-white"
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  activeNav === "portfolio"
                    ? "bg-cyber-blue shadow-[0_0_12px_rgba(0,212,255,0.8)]"
                    : "bg-[rgba(139,146,176,0.4)]"
                }`}
              />
              <span className="flex-1">Portfolio Overview</span>
              {activeNav === "portfolio" && (
                <span className="text-[11px] px-2 py-0.5 rounded-full border border-[rgba(0,212,255,0.6)] text-cyber-blue bg-[rgba(0,212,255,0.1)]">
                  Live
                </span>
              )}
            </li>
            <li
              onClick={() => onNavChange("industry")}
              className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-[10px] text-sm cursor-pointer transition-all duration-200 ${
                activeNav === "industry"
                  ? "text-white bg-gradient-to-r from-[rgba(0,212,255,0.35)] to-transparent shadow-[0_8px_30px_rgba(0,212,255,0.35)]"
                  : "text-[#8b92b0] hover:bg-[rgba(26,31,58,0.9)] hover:text-white"
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  activeNav === "industry"
                    ? "bg-cyber-blue shadow-[0_0_12px_rgba(0,212,255,0.8)]"
                    : "bg-[rgba(139,146,176,0.4)]"
                }`}
              />
              <span className="flex-1">Industry Data</span>
              {activeNav === "industry" && (
                <span className="text-[11px] px-2 py-0.5 rounded-full border border-[rgba(139,92,246,0.6)] text-blockchain-purple bg-[rgba(139,92,246,0.12)]">
                  New
                </span>
              )}
            </li>
            <li
              onClick={() => onNavChange("cohorts")}
              className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-[10px] text-sm cursor-pointer transition-all duration-200 ${
                activeNav === "cohorts"
                  ? "text-white bg-gradient-to-r from-[rgba(0,212,255,0.35)] to-transparent shadow-[0_8px_30px_rgba(0,212,255,0.35)]"
                  : "text-[#8b92b0] hover:bg-[rgba(26,31,58,0.9)] hover:text-white"
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  activeNav === "cohorts"
                    ? "bg-cyber-blue shadow-[0_0_12px_rgba(0,212,255,0.8)]"
                    : "bg-[rgba(139,146,176,0.4)]"
                }`}
              />
              <span className="flex-1">Loan Cohorts</span>
            </li>
            <li
              onClick={() => onNavChange("regions")}
              className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-[10px] text-sm cursor-pointer transition-all duration-200 ${
                activeNav === "regions"
                  ? "text-white bg-gradient-to-r from-[rgba(0,212,255,0.35)] to-transparent shadow-[0_8px_30px_rgba(0,212,255,0.35)]"
                  : "text-[#8b92b0] hover:bg-[rgba(26,31,58,0.9)] hover:text-white"
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  activeNav === "regions"
                    ? "bg-cyber-blue shadow-[0_0_12px_rgba(0,212,255,0.8)]"
                    : "bg-[rgba(139,146,176,0.4)]"
                }`}
              />
              <span className="flex-1">Regions & Risk</span>
            </li>
          </ul>

          {/* <div className="text-[11px] uppercase tracking-[0.16em] text-[#8b92b0] mt-2 mb-1">
            Infrastructure
          </div>
          <ul className="flex flex-col gap-1.5 list-none">
            <li className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-[10px] text-sm text-[#8b92b0] cursor-pointer transition-all duration-200 hover:bg-[rgba(26,31,58,0.9)] hover:text-white">
              <span className="w-2 h-2 rounded-full bg-[rgba(139,146,176,0.4)]" />
              <span className="flex-1">Data Partners</span>
            </li>
            <li className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-[10px] text-sm text-[#8b92b0] cursor-pointer transition-all duration-200 hover:bg-[rgba(26,31,58,0.9)] hover:text-white">
              <span className="w-2 h-2 rounded-full bg-[rgba(139,146,176,0.4)]" />
              <span className="flex-1">API Usage</span>
            </li>
          </ul> */}
        </div>
      </aside>
    </>
  );
};
